import React, { createContext, useReducer, useEffect } from 'react';
import AppReducer from './AppReducer';
import * as api from '../actions';

const POLL_INTERVAL_MS = 3000;
const STORAGE_KEY = 'crds:session';

const initialState = {
  game: null,
  players: [],
  currentPlayerId: null,
};

// Rehydrate the player's identity + game code from a previous visit so a page
// refresh keeps them in the game instead of dropping them at the join screen.
function loadInitialState() {
  try {
    const saved = JSON.parse(localStorage.getItem(STORAGE_KEY) || 'null');
    if (saved && saved.code && saved.currentPlayerId != null) {
      // game is a skeleton (just the code); the poll fills in the rest.
      return { game: { code: saved.code }, players: [], currentPlayerId: saved.currentPlayerId };
    }
  } catch (e) {
    /* ignore malformed storage */
  }
  return initialState;
}

function clearSession() {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch (e) {
    /* ignore */
  }
}

export const GlobalContext = createContext(initialState);

export const GlobalProvider = ({ children }) => {
  const [state, dispatch] = useReducer(AppReducer, undefined, loadInitialState);

  const gameCode = state.game?.code;
  const currentPlayerId = state.currentPlayerId;
  // The backend has no "finished" status; a set winner means the game is over.
  const gameOver = Boolean(state.game?.winner);

  // Persist the session whenever the identity is known.
  useEffect(() => {
    if (gameCode && currentPlayerId != null) {
      try {
        localStorage.setItem(
          STORAGE_KEY,
          JSON.stringify({ code: gameCode, currentPlayerId })
        );
      } catch (e) {
        /* ignore */
      }
    }
  }, [gameCode, currentPlayerId]);

  useEffect(() => {
    if (!gameCode || gameOver) return;

    let cancelled = false;
    const poll = async () => {
      try {
        const data = await api.getGame(gameCode);
        if (cancelled) return;
        if (data && data.status != null) {
          dispatch({ type: 'GAME_STATE_UPDATED', payload: data });
        } else {
          // The game no longer exists — drop the stale session.
          clearSession();
          dispatch({ type: 'RESET_GAME' });
        }
      } catch (error) {
        // Transient poll failures are ignored; the next tick retries.
      }
    };

    poll();
    const id = setInterval(poll, POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [gameCode, gameOver]);

  async function newGame(name) {
    dispatch({ type: 'NEW_GAME_PENDING' });
    try {
      const gameData = await api.createGame(name);
      dispatch({ type: 'NEW_GAME_SUCCESS', payload: gameData });
      return gameData;
    } catch (error) {
      dispatch({ type: 'NEW_GAME_ERROR', payload: error.message });
      return null;
    }
  }

  async function joinGame(code, name) {
    dispatch({ type: 'JOIN_GAME_PENDING' });
    try {
      const gameData = await api.joinGame(code, name);
      dispatch({ type: 'JOIN_GAME_SUCCESS', payload: gameData });
      return gameData;
    } catch (error) {
      dispatch({ type: 'JOIN_GAME_ERROR', payload: error.message });
      return null;
    }
  }

  // startGame and pickWinner return an empty body, so re-fetch the game after.
  async function refresh() {
    const data = await api.getGame(gameCode);
    dispatch({ type: 'GAME_STATE_UPDATED', payload: data });
  }

  async function startGame() {
    try {
      await api.startGame(gameCode);
      await refresh();
    } catch (error) {
      dispatch({ type: 'GAME_ERROR', payload: error.message });
    }
  }

  async function playCard(card) {
    try {
      const data = await api.playCard(state.currentPlayerId, card);
      dispatch({ type: 'GAME_STATE_UPDATED', payload: data });
    } catch (error) {
      dispatch({ type: 'GAME_ERROR', payload: error.message });
    }
  }

  async function pickWinner(winnerPlayerId) {
    try {
      await api.pickWinner(winnerPlayerId);
      await refresh();
    } catch (error) {
      dispatch({ type: 'GAME_ERROR', payload: error.message });
    }
  }

  // Owner-only: remove another player from the game.
  async function removePlayer(playerId) {
    try {
      await api.quitPlayer(playerId);
      await refresh();
    } catch (error) {
      dispatch({ type: 'GAME_ERROR', payload: error.message });
    }
  }

  function resetGame() {
    clearSession();
    dispatch({ type: 'RESET_GAME' });
  }

  return (
    <GlobalContext.Provider
      value={{
        game: state.game,
        players: state.players,
        currentPlayerId: state.currentPlayerId,
        newGame,
        joinGame,
        startGame,
        playCard,
        pickWinner,
        removePlayer,
        resetGame,
      }}
    >
      {children}
    </GlobalContext.Provider>
  );
};
